import { useEffect, useRef, useState } from "react";
import api from "@/admin/api";
import { useAuth } from "@/admin/AuthContext";
import { Eyebrow } from "@/components/comiq/Eyebrow";
import { StatusBadge } from "@/components/comiq/StatusBadge";
import { COLORS } from "@/comiq/tokens";
import { toast } from "sonner";
import { Upload, Download, Ban, RotateCcw, Copy, Info, Send, MailPlus, MailOpen, Plus, X } from "lucide-react";

const INV_STATUS = { invited: "invited", opened: "invited", in_progress: "progress", submitted: "complete", expired: "expired", revoked: "expired" };
const INV_LABEL = { invited: "Diundang", opened: "Dibuka", in_progress: "Berjalan", submitted: "Terkirim", expired: "Kedaluwarsa", revoked: "Dicabut" };
const SEND_KIND = { ready: "invited", sent: "complete", failed: "expired" };
const SEND_LABEL = { ready: "Siap kirim", sent: "Terkirim", failed: "Gagal" };

function downloadCsv(rows, filename) {
  const header = "participant_code,organization,name,email,link\n";
  const body = rows.map((r) => [r.participant_code, r.organization, r.name, r.email, `${window.location.origin}/s/${r.token}`].map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob([header + body], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function Invitations() {
  const { user } = useAuth();
  const [waves, setWaves] = useState([]);
  const [waveId, setWaveId] = useState("");
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [freshLinks, setFreshLinks] = useState([]);
  const [sendingId, setSendingId] = useState(null);
  const [confirmResend, setConfirmResend] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ org_id: "", name: "", email: "" });
  const [orgs, setOrgs] = useState([]);
  const [studies, setStudies] = useState([]);
  const fileRef = useRef(null);
  const isAnalyst = user?.role === "analyst";

  useEffect(() => {
    api.get("/admin/waves").then((r) => {
      setWaves(r.data);
      const pick = r.data.find((w) => w.is_demo && w.status !== "archived") || r.data.find((w) => w.status !== "archived");
      if (pick) setWaveId(pick.wave_id);
    }).catch(() => {});
    api.get("/admin/organizations").then((r) => setOrgs(r.data)).catch(() => {});
    api.get("/admin/studies").then((r) => setStudies(r.data)).catch(() => {});
  }, []);

  const load = (id) => {
    if (!id) return;
    api.get(`/admin/waves/${id}/invitations`).then((r) => setRows(r.data)).catch(() => {});
  };
  useEffect(() => load(waveId), [waveId]);

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/waves/${waveId}/invitations/import`, fd);
      const { created, errors } = r.data;
      if (created.length) {
        setFreshLinks(created);
        toast.success(`${created.length} undangan dibuat — tinjau lalu unduh CSV tautan secara manual`);
      }
      errors.forEach((er) => toast.warning(`Baris ${er.row}: ${er.error}`));
      if (!created.length && !errors.length) toast.info("Tidak ada baris baru");
      load(waveId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impor gagal");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const revoke = async (id) => {
    try {
      await api.post(`/admin/invitations/${id}/revoke`);
      toast.success("Undangan dicabut");
      load(waveId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mencabut");
    }
  };

  const regenerate = async (row) => {
    try {
      const r = await api.post(`/admin/invitations/${row.invitation_id}/regenerate`);
      const link = `${window.location.origin}/s/${r.data.token}`;
      setFreshLinks((p) => [...p, { participant_code: r.data.participant_code, organization: row.org_name, name: row.contact_name, email: row.email, token: r.data.token }]);
      await navigator.clipboard.writeText(link).catch(() => {});
      toast.success("Token dirotasi — tautan baru disalin & masuk daftar unduhan");
      load(waveId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal merotasi token");
    }
  };

  const sendEmail = async (row) => {
    setSendingId(row.invitation_id);
    try {
      const r = await api.post(`/admin/invitations/${row.invitation_id}/send-email`);
      toast.success(`Email terkirim ke ${r.data.sent_to} — token dirotasi, tautan lama tidak berlaku`);
      load(waveId);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal mengirim email"); }
    finally { setSendingId(null); }
  };

  const resendEmail = async (row) => {
    setSendingId(row.invitation_id);
    try {
      const r = await api.post(`/admin/invitations/${row.invitation_id}/resend-email`, { confirm: true });
      toast.success(`Email dikirim ulang ke ${r.data.sent_to}`);
      setConfirmResend(null);
      load(waveId);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal mengirim ulang"); }
    finally { setSendingId(null); }
  };

  const testEmail = async (row) => {
    setSendingId(row.invitation_id);
    try {
      const r = await api.post(`/admin/invitations/${row.invitation_id}/test-email`);
      toast.success(`Email UJI terkirim ke ${r.data.sent_to} (pratinjau — status undangan tidak berubah)`);
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal mengirim email uji"); }
    finally { setSendingId(null); }
  };

  const createOne = async (e) => {
    e.preventDefault();
    if (!addForm.org_id) return toast.error("Pilih organisasi terlebih dahulu");
    try {
      const r = await api.post(`/admin/waves/${waveId}/invitations`, addForm);
      toast.success(`Undangan ${r.data.participant_code} dibuat — status Siap kirim. Klik "Kirim email" pada tabel untuk mengirim.`);
      setShowAdd(false);
      setAddForm({ org_id: "", name: "", email: "" });
      load(waveId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal membuat undangan");
    }
  };

  const inputCls = "rounded-comiq-sm border bg-white px-3 py-2 text-sm outline-none";
  const activeWave = waves.find((w) => w.wave_id === waveId);
  const activeStudy = activeWave ? studies.find((s) => s.study_id === activeWave.study_id) : null;

  return (
    <div className="p-6">
      <Eyebrow>Distribusi Survei</Eyebrow>
      <h1 className="font-serif-editorial mt-1 text-3xl" style={{ color: COLORS.ink }}>Undangan responden</h1>

      <div className="mt-4 flex items-start gap-2.5 rounded-comiq border p-4 text-sm" style={{ borderColor: "#F5C69B", background: "#FFF4E8", color: "#8A4B0F" }} data-testid="email-notice">
        <Info size={16} className="mt-0.5 shrink-0" />
        <span>
          Pengiriman email <strong>aktif (mode pratinjau)</strong> via tombol per undangan — tidak ada pengiriman otomatis saat impor.
          Token disimpan sebagai hash; token mentah hanya ada di dalam email yang terkirim. Mengirim/mengirim ulang email akan <strong>merotasi token</strong> (tautan lama tidak berlaku).
        </span>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <select value={waveId} onChange={(e) => setWaveId(e.target.value)} className={inputCls} style={{ borderColor: COLORS.line, color: COLORS.ink }} data-testid="wave-select">
          <option value="">Pilih gelombang…</option>
          {waves.map((w) => (
            <option key={w.wave_id} value={w.wave_id}>{w.label}{w.is_demo ? " (demo)" : ""}</option>
          ))}
        </select>
        {!isAnalyst && (
          <>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={doImport} data-testid="csv-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={!waveId || importing} className="btn-comiq btn-accent disabled:opacity-40" data-testid="import-csv-btn">
              <Upload size={15} /> {importing ? "Mengimpor…" : "Impor CSV responden"}
            </button>
            <button onClick={() => setShowAdd(true)} disabled={!waveId} className="btn-comiq btn-line disabled:opacity-40" data-testid="add-invitation-btn">
              <Plus size={15} /> Tambah undangan
            </button>
            {freshLinks.length > 0 && (
              <button onClick={() => downloadCsv(freshLinks, `tautan-undangan-${waveId}-terbaru.csv`)} className="btn-comiq btn-line" data-testid="download-links-btn">
                <Download size={15} /> Unduh {freshLinks.length} tautan baru
              </button>
            )}
          </>
        )}
        <span className="text-xs" style={{ color: COLORS.textSoft }}>Format CSV: organization, name, email</span>
      </div>

      {freshLinks.length > 0 && (
        <div className="mt-5 rounded-comiq border p-4" style={{ borderColor: "#CDE7D2", background: "#F1FAF3" }} data-testid="fresh-links-panel">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold" style={{ color: "#15803D" }}>{freshLinks.length} tautan sekali-tampil — salin atau unduh secara manual</span>
            <button onClick={() => downloadCsv(freshLinks, `tautan-undangan-${waveId}.csv`)} className="btn-comiq btn-line" data-testid="download-fresh-btn"><Download size={14} /> Unduh CSV</button>
          </div>
          <div className="mt-3 max-h-44 space-y-1.5 overflow-y-auto">
            {freshLinks.map((l) => (
              <div key={l.participant_code} className="flex items-center gap-2 rounded-comiq-sm bg-white px-3 py-2 text-xs" style={{ border: `1px solid ${COLORS.line}` }}>
                <span className="font-mono font-semibold" style={{ color: COLORS.ink }}>{l.participant_code}</span>
                <span className="truncate" style={{ color: COLORS.textSoft }}>{`${window.location.origin}/s/${l.token}`}</span>
                <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/s/${l.token}`); toast.success("Tautan disalin"); }} className="ml-auto rounded-full border p-1" style={{ borderColor: COLORS.line }} title="Salin tautan"><Copy size={12} style={{ color: COLORS.ink }} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="add-invitation-modal">
          <div className="w-full max-w-md rounded-comiq-lg border bg-comiq-surface-alt p-6" style={{ borderColor: COLORS.line }}>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold" style={{ color: COLORS.ink }}>Tambah undangan</h3>
              <button onClick={() => setShowAdd(false)} data-testid="add-invitation-cancel"><X size={16} style={{ color: COLORS.textSoft }} /></button>
            </div>
            <p className="mt-2 rounded-comiq-sm border px-3 py-2 text-xs" style={{ borderColor: COLORS.line, color: COLORS.textSoft }} data-testid="add-invitation-context">
              Studi: <strong style={{ color: COLORS.ink }}>{activeStudy?.title || "—"}</strong> · Gelombang: <strong style={{ color: COLORS.ink }}>{activeWave?.label || "—"}</strong>
            </p>
            <form onSubmit={createOne} className="mt-4 space-y-3">
              <label className="block">
                <span className="text-xs font-semibold" style={{ color: COLORS.ink }}>Organisasi *</span>
                <select value={addForm.org_id} onChange={(e) => setAddForm({ ...addForm, org_id: e.target.value })} required className={`${inputCls} mt-1 w-full`} style={{ borderColor: COLORS.line, color: COLORS.ink }} data-testid="add-invitation-org">
                  <option value="">Pilih organisasi…</option>
                  {orgs.map((o) => <option key={o.org_id} value={o.org_id}>{o.name}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-xs font-semibold" style={{ color: COLORS.ink }}>Nama responden (opsional)</span>
                <input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} className={`${inputCls} mt-1 w-full`} style={{ borderColor: COLORS.line, color: COLORS.ink }} data-testid="add-invitation-name" />
              </label>
              <label className="block">
                <span className="text-xs font-semibold" style={{ color: COLORS.ink }}>Email responden *</span>
                <input type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} required className={`${inputCls} mt-1 w-full`} style={{ borderColor: COLORS.line, color: COLORS.ink }} data-testid="add-invitation-email" />
              </label>
              <p className="text-[11px]" style={{ color: COLORS.textSoft }}>
                Token disimpan sebagai hash dan tautan tidak ditampilkan di sini — setelah dibuat, kirim lewat tombol "Kirim email" pada tabel. Tidak ada email yang dikirim otomatis.
              </p>
              <button className="btn-comiq btn-accent w-full justify-center" data-testid="add-invitation-submit">Buat undangan</button>
            </form>
          </div>
        </div>
      )}

      <div className="mt-6 overflow-hidden rounded-comiq border bg-comiq-surface-alt" style={{ borderColor: COLORS.line }}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="invitations-table">
            <thead>
              <tr style={{ background: COLORS.surface, color: COLORS.textSoft }}>
                {["Kode", "Organisasi", "Nama", "Email", "Status", "Pengiriman", "Kedaluwarsa", "Aksi"].map((h) => (
                  <th key={h} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm" style={{ color: COLORS.textSoft }}>Belum ada undangan pada gelombang ini.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.invitation_id} className="border-t" style={{ borderColor: COLORS.line }} data-testid={`inv-row-${r.participant_code}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold" style={{ color: COLORS.ink }}>{r.participant_code}</td>
                  <td className="whitespace-nowrap px-4 py-3" style={{ color: COLORS.ink }}>{r.org_name}</td>
                  <td className="px-4 py-3" style={{ color: COLORS.textSoft }}>{r.contact_name || "—"}</td>
                  <td className="px-4 py-3" style={{ color: COLORS.textSoft }}>{r.email || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge kind={INV_STATUS[r.status] || "invited"} testId={`inv-status-${r.participant_code}`}>{INV_LABEL[r.status] || r.status}</StatusBadge></td>
                  <td className="px-4 py-3" data-testid={`send-cell-${r.participant_code}`}>
                    <StatusBadge kind={SEND_KIND[r.send_status] || "invited"} testId={`send-status-${r.participant_code}`}>{SEND_LABEL[r.send_status] || "Siap kirim"}</StatusBadge>
                    {r.sent_at && <div className="mt-1 whitespace-nowrap text-[10px]" style={{ color: COLORS.textSoft }}>{new Date(r.sent_at).toLocaleString("id-ID")}{r.sent_to_masked ? ` → ${r.sent_to_masked}` : ""}</div>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs" style={{ color: COLORS.textSoft }}>{new Date(r.expires_at).toLocaleDateString("id-ID")}</td>
                  <td className="px-4 py-3">
                    {!isAnalyst && (
                      <div className="flex gap-1.5">
                        {r.status === "invited" && r.email && new Date(r.expires_at) > new Date() && r.send_status !== "sent" && (
                          <button onClick={() => sendEmail(r)} disabled={sendingId === r.invitation_id} title="Kirim email undangan (merotasi token — tautan lama tidak berlaku)" className="rounded-full border p-1.5 transition-colors hover:bg-black/5 disabled:opacity-40" style={{ borderColor: COLORS.line }} data-testid={`send-${r.participant_code}`}>
                            <Send size={13} style={{ color: "#15803D" }} />
                          </button>
                        )}
                        {r.send_status === "sent" && r.status === "invited" && (
                          confirmResend === r.invitation_id ? (
                            <button onClick={() => resendEmail(r)} disabled={sendingId === r.invitation_id} className="whitespace-nowrap rounded-full border px-2 py-1 text-[10px] font-semibold" style={{ borderColor: COLORS.orange, color: COLORS.orangeInk }} data-testid={`resend-confirm-${r.participant_code}`}>Yakin kirim ulang?</button>
                          ) : (
                            <button onClick={() => setConfirmResend(r.invitation_id)} title="Kirim ulang email (butuh konfirmasi; merotasi token)" className="rounded-full border p-1.5 transition-colors hover:bg-black/5" style={{ borderColor: COLORS.line }} data-testid={`resend-${r.participant_code}`}>
                              <MailPlus size={13} style={{ color: COLORS.ink }} />
                            </button>
                          )
                        )}
                        <button onClick={() => testEmail(r)} disabled={sendingId === r.invitation_id} title={`Kirim email UJI ke email saya (${user?.email}) — tidak mengubah status`} className="rounded-full border p-1.5 transition-colors hover:bg-black/5 disabled:opacity-40" style={{ borderColor: COLORS.line }} data-testid={`testsend-${r.participant_code}`}>
                          <MailOpen size={13} style={{ color: COLORS.textSoft }} />
                        </button>
                        {r.status !== "submitted" && r.status !== "revoked" && (
                          <>
                            <button onClick={() => regenerate(r)} title="Rotasi token (tautan baru)" className="rounded-full border p-1.5 transition-colors hover:bg-black/5" style={{ borderColor: COLORS.line }} data-testid={`regen-${r.participant_code}`}>
                              <RotateCcw size={13} style={{ color: COLORS.ink }} />
                            </button>
                            <button onClick={() => revoke(r.invitation_id)} title="Cabut undangan" className="rounded-full border p-1.5 transition-colors hover:bg-black/5" style={{ borderColor: COLORS.line }} data-testid={`revoke-${r.participant_code}`}>
                              <Ban size={13} style={{ color: "#B42318" }} />
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
